import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RAG_META } from "@/lib/rag";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Download, ArrowUp, ArrowDown, Mail } from "lucide-react";
import LocalDateTimeField from "@/components/LocalDateTimeField";
import { fmtHM, fmtDate, fmtDT, todayLocal, fmtTzAbbr, exportFilename } from "@/lib/timefmt";

const RAG_ORDER = { green: 0, yellow: 1, amber: 2, red: 3 };
const MY_EXPORTS = [
  { id: "new", label: "All new entries" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "all", label: "All history" },
];
const hm2min = (t) => { if (!t) return -1; const [h, m] = String(t).split(":").map(Number); return (h || 0) * 60 + (m || 0); };

function SortTh({ label, k, sort, setSort, className = "", right = false }) {
  const active = sort.key === k;
  return (
    <th className={`px-4 py-2.5 text-[10px] uppercase tracking-widest text-slate-400 font-mono-num font-medium ${right ? "text-right" : ""} ${className}`}>
      <button onClick={() => setSort((s) => ({ key: k, dir: active && s.dir === "asc" ? "desc" : "asc" }))}
        data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 hover:text-slate-200 transition-colors ${active ? "text-blue-400" : ""}`}>
        {label}
        {active && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function FilterTh({ value, onChange, testId }) {
  return (
    <th className="px-2 pb-2 pt-0 font-normal">
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder="Filter…"
        className="w-full px-2 py-1 rounded bg-slate-950 border border-slate-800 text-xs font-normal normal-case tracking-normal" />
    </th>
  );
}

const filterRows = (rows, textOf, filters) => rows.filter((e) => {
  const t = textOf(e);
  return Object.entries(filters).every(([k, v]) => !v || String(t[k] ?? "").toLowerCase().includes(v.toLowerCase()));
});

const sortRows = (rows, sort, keyOf) => [...rows].sort((a, b) => {
  const va = keyOf(a, sort.key);
  const vb = keyOf(b, sort.key);
  const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
  return sort.dir === "asc" ? cmp : -cmp;
});

const dutyText = (e) => (e.duty_start || e.duty_end ? `${e.duty_start || "—"} → ${e.duty_end || "—"}` : "—");
const riskText = (e) => (RAG_META[e.rag] || {}).label || "—";

const myText = (e) => ({
  date: fmtDate(e.date), duty: dutyText(e), tz: fmtTzAbbr(e.timezone, e.date) || "—",
  risk: riskText(e), score: Number(e.score_total).toFixed(1),
});
const myKey = (e, k) => (
  k === "date" ? e.date
    : k === "duty" ? hm2min(e.duty_start)
    : k === "tz" ? myText(e).tz
    : k === "risk" ? (RAG_ORDER[e.rag] ?? -1)
    : Number(e.score_total) || 0
);

const orgText = (e) => ({
  date: fmtDate(e.date), member: e.member_name || "",
  org: [e.unit_name, e.subunit_name, e.team_name].filter(Boolean).join(" / ") || "—",
  duty: dutyText(e), risk: riskText(e), score: Number(e.score_total).toFixed(1),
});
const orgKey = (e, k) => (
  k === "date" ? e.date
    : k === "member" ? (e.member_name || "").toLowerCase()
    : k === "org" ? orgText(e).org.toLowerCase()
    : k === "duty" ? hm2min(e.duty_start)
    : k === "risk" ? (RAG_ORDER[e.rag] ?? -1)
    : Number(e.score_total) || 0
);

export default function HistoryPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [view, setView] = useState("mine");
  const [orgEntries, setOrgEntries] = useState([]);
  const [rangeMode, setRangeMode] = useState("this_week");
  const [weekAnchor, setWeekAnchor] = useState("");
  const [monthVal, setMonthVal] = useState("");
  const [mySort, setMySort] = useState({ key: "date", dir: "desc" });
  const [myFilters, setMyFilters] = useState({ date: "", duty: "", tz: "", risk: "", score: "" });
  const [orgSort, setOrgSort] = useState({ key: "date", dir: "desc" });
  const [orgFilters, setOrgFilters] = useState({ date: "", member: "", org: "", duty: "", risk: "", score: "" });
  const canViewOrg = ["command", "supervisor"].includes(user?.privilege) || user?.role === "admin";
  const [open, setOpen] = useState(null);
  const [restLocOpts, setRestLocOpts] = useState([]);
  const [restQualOpts, setRestQualOpts] = useState([]);
  const [addForms, setAddForms] = useState({});
  const load = () => api.get("/entries").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!user?.role_id) return;
    api.get(`/options?role_id=${user.role_id}`).then((r) => {
      setRestLocOpts(r.data.filter((o) => o.question === "rest_location"));
      setRestQualOpts(r.data.filter((o) => o.question === "rest_quality"));
    });
  }, [user]);

  const pad2 = (x) => String(x).padStart(2, "0");
  const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - (x.getDay() + 6) % 7); return x; };
  const range = (() => {
    const now = new Date();
    if (rangeMode === "this_week") { const m = mondayOf(now); const s = new Date(m); s.setDate(m.getDate() + 6); return [isoOf(m), isoOf(s)]; }
    if (rangeMode === "week" && weekAnchor) { const m = mondayOf(new Date(`${weekAnchor}T00:00:00`)); const s = new Date(m); s.setDate(m.getDate() + 6); return [isoOf(m), isoOf(s)]; }
    if (rangeMode === "month" && monthVal) { const [y, mo] = monthVal.split("-").map(Number); return [`${y}-${pad2(mo)}-01`, isoOf(new Date(y, mo, 0))]; }
    if (rangeMode === "this_month") { return [isoOf(new Date(now.getFullYear(), now.getMonth(), 1)), isoOf(new Date(now.getFullYear(), now.getMonth() + 1, 0))]; }
    return [null, null];
  })();

  useEffect(() => {
    if (view !== "org" || !range[0]) return;
    api.get(`/entries/org?from_date=${range[0]}&to_date=${range[1]}`)
      .then((r) => setOrgEntries(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [view, range[0], range[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  const myView = sortRows(filterRows(entries, myText, myFilters), mySort, myKey);
  const orgView = sortRows(filterRows(orgEntries, orgText, orgFilters), orgSort, orgKey);

  const emailOrg = async (address) => {
    if (!range[0]) { toast.error("Pick a range first"); return; }
    try {
      const { data } = await api.post("/entries/org/export/email", { from_date: range[0], to_date: range[1], address, fmt: orgExpFmt });
      toast.success(`Emailed ${data.count} entr${data.count === 1 ? "y" : "ies"} to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportOrg = async (fmt) => {
    if (!range[0]) { toast.error("Pick a range first"); return; }
    try {
      const res = await api.get("/entries/org/export", { params: { from_date: range[0], to_date: range[1], fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename("fatigue-history-org", fmt, user);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const setAddField = (eid, patch) => setAddForms((f) => ({
    ...f,
    [eid]: { start_time: "", stop_time: "", location_option_id: "", quality_option_id: "", ...(f[eid] || {}), ...patch },
  }));

  const submitRest = async (e) => {
    const f = addForms[e.id];
    if (!f?.start_time || !f?.stop_time || !f?.location_option_id || !f?.quality_option_id) {
      toast.error("Fill all rest period fields");
      return;
    }
    try {
      await api.post(`/entries/${e.id}/rest`, {
        start_time: new Date(f.start_time).toISOString(),
        stop_time: new Date(f.stop_time).toISOString(),
        location_option_id: f.location_option_id,
        quality_option_id: f.quality_option_id,
      });
      toast.success("Rest period added");
      setAddForms((af) => ({ ...af, [e.id]: undefined }));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteEntry = async (e) => {
    if (!window.confirm(`Delete today's entry (${fmtDate(e.date)})? This cannot be undone.`)) return;
    try {
      await api.delete(`/entries/${e.id}`);
      toast.success("Entry deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setMyF = (k) => (v) => setMyFilters((f) => ({ ...f, [k]: v }));
  const setOrgF = (k) => (v) => setOrgFilters((f) => ({ ...f, [k]: v }));

  const [myExp, setMyExp] = useState("new");
  const [myExpFmt, setMyExpFmt] = useState("csv");
  const [myExpBusy, setMyExpBusy] = useState(false);
  const [orgExpFmt, setOrgExpFmt] = useState("csv");

  const myExportParams = () => {
    const params = {};
    if (myExp === "new") {
      params.mode = "not_exported";
    } else if (myExp === "7d" || myExp === "30d") {
      const d = new Date(`${todayLocal()}T00:00:00`);
      d.setDate(d.getDate() - (myExp === "7d" ? 6 : 29));
      params.mode = "period";
      params.from_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      params.to_date = todayLocal();
      params.include_exported = true;
    } else {
      params.mode = "all";
      params.include_exported = true;
    }
    return params;
  };

  const emailMine = async (address) => {
    setMyExpBusy(true);
    try {
      const { data } = await api.post("/export/my/email", { ...myExportParams(), address, fmt: myExpFmt });
      toast.success(`Emailed ${data.count} entr${data.count === 1 ? "y" : "ies"} to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setMyExpBusy(false); }
  };

  const exportMine = async () => {
    setMyExpBusy(true);
    try {
      const params = { ...myExportParams(), fmt: myExpFmt };
      const res = await api.get("/export/my", { params, responseType: "blob" });
      const count = res.headers["x-export-count"];
      const mime = myExpFmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename(`fatigue-${myExp}`, myExpFmt, user);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${count ?? "?"} entr${count === "1" ? "y" : "ies"}`);
    } catch (e) {
      let msg = "Export failed";
      try {
        if (e.response?.data instanceof Blob) {
          msg = JSON.parse(await e.response.data.text())?.detail || msg;
        } else {
          msg = formatApiErrorDetail(e.response?.data?.detail);
        }
      } catch { /* keep default message */ }
      toast.error(typeof msg === "string" ? msg : "Export failed");
    } finally { setMyExpBusy(false); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Logbook</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Fatigue History</h1>
        <p className="text-slate-400 text-sm mt-1">Every daily entry you have submitted. Click a column header to sort; use the filter row to narrow down.</p>
        {canViewOrg && (
          <div className="flex gap-1 mt-4 border-b border-slate-800" data-testid="history-view-tabs">
            {[["mine", "My entries"], ["org", "Org unit & below"]].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} data-testid={`history-view-${id}`}
                className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${view === id ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {view === "org" && canViewOrg && (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="history-org-toolbar">
            <select value={rangeMode} onChange={(e) => setRangeMode(e.target.value)} data-testid="history-range"
              className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
              <option value="this_week">This week</option>
              <option value="week">By week…</option>
              <option value="month">By month…</option>
              <option value="this_month">This month</option>
            </select>
            {rangeMode === "week" && (
              <input type="date" value={weekAnchor} onChange={(e) => setWeekAnchor(e.target.value)} data-testid="history-week-anchor"
                title="Pick any day in the week"
                className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm" />
            )}
            {rangeMode === "month" && (
              <input type="month" value={monthVal} onChange={(e) => setMonthVal(e.target.value)} data-testid="history-month"
                className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm" />
            )}
            <select value={orgExpFmt} onChange={(e) => setOrgExpFmt(e.target.value)} data-testid="history-org-export-format"
              title="Export format"
              className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
            <button onClick={() => exportOrg(orgExpFmt)} disabled={!orgView.length} data-testid="history-org-export-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-40 transition-colors">
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            {["personal", "work"].map((a) => (
              <button key={a} onClick={() => emailOrg(a)}
                disabled={!orgView.length || !(a === "work" ? user?.work_email : user?.personal_email)}
                data-testid={`history-org-export-email-${a}`}
                title={(a === "work" ? user?.work_email : user?.personal_email) || `Set a ${a} email in Profile → Email addresses`}
                className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-md bg-slate-700 hover:bg-slate-600 border border-slate-500 text-sm font-medium text-slate-100 shadow-sm disabled:opacity-40 transition-colors">
                <Mail className="h-3.5 w-3.5" /> Email {a === "work" ? "Work" : "Personal"}
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden overflow-x-auto">
            <table className="w-full" data-testid="history-org-table">
              <thead>
                <tr className="border-b border-slate-800/60 text-left">
                  <SortTh label="Date" k="date" sort={orgSort} setSort={setOrgSort} />
                  <SortTh label="Member" k="member" sort={orgSort} setSort={setOrgSort} />
                  <SortTh label="Org" k="org" sort={orgSort} setSort={setOrgSort} />
                  <SortTh label="Duty" k="duty" sort={orgSort} setSort={setOrgSort} />
                  <SortTh label="Risk" k="risk" sort={orgSort} setSort={setOrgSort} />
                  <SortTh label="Score" k="score" sort={orgSort} setSort={setOrgSort} right />
                </tr>
                <tr className="border-b border-slate-800">
                  <FilterTh value={orgFilters.date} onChange={setOrgF("date")} testId="history-org-filter-date" />
                  <FilterTh value={orgFilters.member} onChange={setOrgF("member")} testId="history-org-filter-member" />
                  <FilterTh value={orgFilters.org} onChange={setOrgF("org")} testId="history-org-filter-org" />
                  <FilterTh value={orgFilters.duty} onChange={setOrgF("duty")} testId="history-org-filter-duty" />
                  <FilterTh value={orgFilters.risk} onChange={setOrgF("risk")} testId="history-org-filter-risk" />
                  <FilterTh value={orgFilters.score} onChange={setOrgF("score")} testId="history-org-filter-score" />
                </tr>
              </thead>
              <tbody>
                {orgView.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-sm text-slate-500">No entries match.</td></tr>
                )}
                {orgView.map((e) => {
                  const meta = RAG_META[e.rag] || { cls: "", dot: "", label: "—" };
                  return (
                    <tr key={e.id} className="border-b border-slate-800/70" data-testid={`hist-org-${e.id}`}>
                      <td className="px-4 py-3 font-mono-num text-sm whitespace-nowrap">{fmtDate(e.date)}</td>
                      <td className="px-4 py-3 text-sm">{e.member_name}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{[e.unit_name, e.subunit_name, e.team_name].filter(Boolean).join(" / ") || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono-num whitespace-nowrap">{dutyText(e)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                          <span className={`inline-block h-2 w-2 rounded-full mr-2 align-middle ${meta.dot}`} />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono-num text-sm text-right">{Number(e.score_total).toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view === "mine" && (
        <div className="mb-4 p-4 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="history-my-export">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Select period</label>
              <select value={myExp} onChange={(e) => setMyExp(e.target.value)} data-testid="history-my-export-period"
                className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
                {MY_EXPORTS.map((o) => <option key={o.id} value={o.id}>{o.id === "all" ? "All records" : o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Select format</label>
              <select value={myExpFmt} onChange={(e) => setMyExpFmt(e.target.value)} data-testid="history-my-export-format"
                className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
                <option value="csv">CSV</option>
                <option value="xlsx">XLSX</option>
              </select>
            </div>
            <button onClick={exportMine} disabled={myExpBusy} data-testid="history-my-export-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium disabled:opacity-60 transition-colors">
              <Download className="h-3.5 w-3.5" /> {myExpBusy ? "Preparing…" : "Download"}
            </button>
            <span className="inline-flex items-center gap-1.5">
              {["personal", "work"].map((a) => (
                <button key={a} onClick={() => emailMine(a)}
                  disabled={myExpBusy || !(a === "work" ? user?.work_email : user?.personal_email)}
                  data-testid={`history-my-export-email-${a}`}
                  title={(a === "work" ? user?.work_email : user?.personal_email) || `Set a ${a} email in Profile → Email addresses`}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-slate-700 hover:bg-slate-600 border border-slate-500 text-xs font-medium text-slate-100 shadow-sm disabled:opacity-40 transition-colors">
                  <Mail className="h-3.5 w-3.5" /> Email {a === "work" ? "Work" : "Personal"}
                </button>
              ))}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Rows include external ID, name, role, org detail, duty start/end and total duty length, fatigue score, and every rest period (start / end / duration / location / quality).
          </p>
        </div>
      )}
      {view === "mine" && entries.length === 0 && (
        <div className="p-6 rounded-lg border border-slate-800 bg-slate-900/60 text-slate-400 text-sm">
          No entries yet.
        </div>
      )}
      {view === "mine" && entries.length > 0 && (
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden overflow-x-auto">
        <table className="w-full table-fixed max-md:block" data-testid="history-table">
          <thead className="max-md:hidden">
            <tr className="border-b border-slate-800/60 text-left">
              <SortTh label="Date" k="date" sort={mySort} setSort={setMySort} className="w-[180px]" />
              <SortTh label="Duty" k="duty" sort={mySort} setSort={setMySort} className="w-[170px]" />
              <SortTh label="Timezone" k="tz" sort={mySort} setSort={setMySort} className="w-[150px]" />
              <SortTh label="Risk" k="risk" sort={mySort} setSort={setMySort} />
              <SortTh label="Score" k="score" sort={mySort} setSort={setMySort} className="w-[80px]" right />
            </tr>
            <tr className="border-b border-slate-800">
              <FilterTh value={myFilters.date} onChange={setMyF("date")} testId="history-filter-date" />
              <FilterTh value={myFilters.duty} onChange={setMyF("duty")} testId="history-filter-duty" />
              <FilterTh value={myFilters.tz} onChange={setMyF("tz")} testId="history-filter-tz" />
              <FilterTh value={myFilters.risk} onChange={setMyF("risk")} testId="history-filter-risk" />
              <FilterTh value={myFilters.score} onChange={setMyF("score")} testId="history-filter-score" />
            </tr>
          </thead>
          <tbody className="max-md:block">
            {myView.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-sm text-slate-500">No entries match the filters.</td></tr>
            )}
            {myView.map((e) => {
              const meta = RAG_META[e.rag];
              const isOpen = open === e.id;
              return (
                <Fragment key={e.id}>
                  <tr onClick={() => setOpen(isOpen ? null : e.id)} data-testid={`hist-toggle-${e.id}`}
                    className="cursor-pointer border-b border-slate-800/70 hover:bg-slate-900/80 transition-colors max-md:grid max-md:grid-cols-2 max-md:items-center max-md:gap-y-1">
                    <td className="px-4 py-3.5 font-mono-num text-sm whitespace-nowrap" data-testid={`hist-${e.id}`}>{fmtDate(e.date)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-400 font-mono-num whitespace-nowrap max-md:text-right" data-testid={`hist-duty-${e.id}`}>
                      {e.duty_start || e.duty_end ? `Duty ${e.duty_start || "—"} → ${e.duty_end || "—"}` : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-400 font-mono-num whitespace-nowrap" data-testid={`hist-tz-${e.id}`}>{fmtTzAbbr(e.timezone, e.date) || "—"}</td>
                    <td className="px-4 py-3.5 max-md:text-right">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-widest ${meta.cls}`}>
                        <span className={`inline-block h-2 w-2 rounded-full mr-2 align-middle ${meta.dot}`} />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-mono-num text-sm text-right max-md:col-span-2 max-md:pt-1 max-md:pb-3 max-md:border-t max-md:border-slate-800/50">{Number(e.score_total).toFixed(1)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-slate-800/70 max-md:block">
                      <td colSpan={5} className="p-4 space-y-3 bg-slate-950/30 max-md:block">
                  {e.date === todayLocal() && (
                    <div className="flex items-center gap-2">
                      <Link to={`/fatigue?edit=${e.id}`} data-testid={`edit-entry-${e.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-600/40 text-xs">
                        Amend today&apos;s entry
                      </Link>
                      <button onClick={() => deleteEntry(e)} data-testid={`delete-entry-${e.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-rose-600/15 hover:bg-rose-600/25 text-rose-300 border border-rose-600/40 text-xs">
                        <Trash2 className="h-3 w-3" /> Delete entry
                      </button>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Breakdown</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {(e.breakdown || []).map((b) => {
                        const m = RAG_META[b.rag];
                        return (
                          <div key={b.question} className={`px-3 py-2 rounded-md text-xs ${m.cls}`}>
                            <div className="opacity-70 font-mono-num uppercase tracking-wider text-[10px]">{b.question}</div>
                            <div>{b.label} <span className="opacity-70 font-mono-num">({b.value})</span></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Rest Periods</div>
                    {(e.rest_periods || []).length === 0 && <div className="text-sm text-slate-500">No rest periods logged.</div>}
                    <div className="space-y-1.5">
                      {(e.rest_periods || []).map((r) => (
                        <div key={r.id} className="text-sm font-mono-num text-slate-300 flex flex-wrap gap-x-4">
                          <span>{fmtDT(r.start_time)} → {fmtDT(r.stop_time)}</span>
                          <span className="text-slate-400">{fmtHM(r.total_hours)} hrs:min</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-800/70" data-testid={`add-rest-form-${e.id}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                        <div>
                          <LocalDateTimeField label="Start" testId={`add-rest-${e.id}-start`} value={addForms[e.id]?.start_time || ""} onChange={(v) => setAddField(e.id, { start_time: v })} disableFuture />
                        </div>
                        <div>
                          <LocalDateTimeField label="Stop" testId={`add-rest-${e.id}-stop`} value={addForms[e.id]?.stop_time || ""} onChange={(v) => setAddField(e.id, { stop_time: v })} disableFuture />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Location</label>
                          <select value={addForms[e.id]?.location_option_id || ""} onChange={(ev) => setAddField(e.id, { location_option_id: ev.target.value })} data-testid={`add-rest-${e.id}-location`}
                            className="w-full px-2 py-1.5 rounded-md bg-slate-950 border border-slate-800 text-xs">
                            <option value="">Select…</option>
                            {restLocOpts.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.value})</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Quality</label>
                          <select value={addForms[e.id]?.quality_option_id || ""} onChange={(ev) => setAddField(e.id, { quality_option_id: ev.target.value })} data-testid={`add-rest-${e.id}-quality`}
                            className="w-full px-2 py-1.5 rounded-md bg-slate-950 border border-slate-800 text-xs">
                            <option value="">Select…</option>
                            {restQualOpts.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.value})</option>)}
                          </select>
                        </div>
                      </div>
                      <button onClick={() => submitRest(e)} data-testid={`add-rest-${e.id}-submit`}
                        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs">
                        <Plus className="h-3 w-3" /> Add Rest Period
                      </button>
                    </div>
                  </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
